/**
 * The role card (§15.2 #3), always behind hold-to-peek. Agents see their teammates and safe
 * bluffs; Delegates see the same four lines with their own text, so the card has the same
 * shape and length for everyone (§15.1).
 */
import { HoldToPeek } from "../components/HoldToPeek.tsx";
import { PlayerChip } from "../components/PlayerChip.tsx";
import { roleName } from "../lib/labels.ts";
import { playerOf, useView } from "../lib/view.ts";
import type { GuestListEntry, PublicView } from "../lib/types.ts";

export function RoleCard() {
  const view = useView();
  const me = view.me;
  const syn = view.syndicate;
  return (
    <HoldToPeek label="Hold to see your role" minHeight="13rem" testId="role-peek">
      <div className="stack" data-testid="role-card">
        <div>
          <div className="kicker">
            {me.team === "syndicate" ? "Agent of the Syndicate" : "Delegate"}
          </div>
          <h2 style={{ fontSize: "1.8rem", margin: 0 }} data-testid="role-name">
            {me.roleName ?? "—"}
          </h2>
          <p className="muted" style={{ margin: 0 }}>
            {me.ability}
          </p>
        </div>
        <div>
          <div className="kicker">{syn ? "Your fellow Agents" : "Who you know"}</div>
          {syn ? (
            syn.teammates.length > 0 ? (
              <ul className="list">
                {syn.teammates.map((t) => (
                  <li key={t.id}>
                    <PlayerChip player={playerOf(view, t.id)} />
                    <span className="faint">{roleName(t.role)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">You are the only Agent.</p>
            )
          ) : (
            <p className="muted">Only yourself. Everyone else must earn your trust.</p>
          )}
        </div>
        <div>
          <div className="kicker">{syn ? "Safe bluffs" : "Your aim"}</div>
          {syn ? (
            <p style={{ margin: 0 }}>
              {syn.bluffs.length > 0 ? syn.bluffs.map(roleName).join(", ") : "None this game."}
            </p>
          ) : (
            <p style={{ margin: 0 }}>Find and exile every Agent before the secrets leak.</p>
          )}
        </div>
      </div>
    </HoldToPeek>
  );
}

export function GuestList({ list }: { list: readonly GuestListEntry[] }) {
  if (list.length === 0) return null;
  return (
    <section className="panel" data-testid="guest-list">
      <h3>The Guest List</h3>
      <p className="faint">
        Every role that might be in play. Two Delegate roles on it are not: the Syndicate's safe
        bluffs.
      </p>
      <ul className="list">
        {list.map((g) => (
          <li key={g.role}>
            {roleName(g.role)}
            {g.oneOrMore ? <span className="faint">(one or more)</span> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function TableRules() {
  return (
    <section className="panel">
      <h3>Table rules</h3>
      <ol style={{ margin: 0, paddingLeft: "1.2rem" }}>
        <li>Never show your screen to another player.</li>
        <li>Say anything you like, including lies.</li>
        <li>Every action happens through the app; a physical handshake alone does nothing.</li>
      </ol>
    </section>
  );
}

export function LeakTrack({ pub }: { pub: PublicView }) {
  if (pub.leakLimit === null) return null;
  const pct = Math.min(100, (pub.leakTrack / pub.leakLimit) * 100);
  return (
    <div data-testid="leak-track">
      <div className="row">
        <span className="grow muted">Leak track</span>
        <strong>
          {pub.leakTrack} / {pub.leakLimit}
        </strong>
      </div>
      <div className="meter" aria-hidden="true">
        <div style={{ width: `${pct}%` }} />
      </div>
      {pub.finalTallyThreshold !== null ? (
        <div className="faint">Final Tally threshold: {pub.finalTallyThreshold}</div>
      ) : null}
    </div>
  );
}
